import { getLoggerManager } from '@/shared/logging'
import type { Disposable } from '@/shared/types/core'

const log = getLoggerManager().getLogger('runtime')

/**
 * 可释放资源作用域。
 * 跟踪 Disposable 资源和延迟清理回调，dispose 时按注册逆序（LIFO）执行。
 * dispose() 采用"捕获并继续"策略：某个资源的 dispose() 抛异常时，
 * 捕获并记录错误，继续释放剩余资源。
 */
export class DisposableScope implements Disposable {
  private cleanups: (() => void)[] = []
  private disposed = false
  /** dispose 过程中捕获的错误（生产环境 console 被移除时仍可通过此属性获取） */
  disposeErrors: unknown[] = []

  /**
   * 跟踪可释放资源，返回原对象（便于链式调用）。
   *
   * @param resource - 待跟踪的可释放资源，dispose 时自动调用其 dispose()
   * @returns 原样返回传入的资源
   * @throws dispose 后调用抛 Error
   */
  track<T extends Disposable>(resource: T): T {
    if (this.disposed) {
      throw new Error('DisposableScope: 已释放，不能再跟踪资源')
    }
    this.cleanups.push(() => resource.dispose())
    return resource
  }

  /**
   * 注册延迟清理回调。
   *
   * @param fn - dispose 时要执行的回调，按注册逆序调用
   * @throws dispose 后调用抛 Error
   */
  defer(fn: () => void): void {
    if (this.disposed) {
      throw new Error('DisposableScope: 已释放，不能再注册回调')
    }
    this.cleanups.push(fn)
  }

  /**
   * 按注册逆序执行清理（LIFO）。重复调用安全（幂等）。
   * "捕获并继续"策略：单个清理抛错不中断剩余清理。
   */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (let i = this.cleanups.length - 1; i >= 0; i--) {
      try {
        this.cleanups[i]()
      } catch (err) {
        this.disposeErrors.push(err)
        log.error(
          `DisposableScope: 清理回调执行出错: ${err instanceof Error ? err.message : String(err)}`
        )
      }
    }
    this.cleanups.length = 0
    if (this.disposeErrors.length > 0) {
      log.error(`DisposableScope: 释放过程中共有 ${this.disposeErrors.length} 个错误`)
    }
  }

  /** 已跟踪的清理动作数量 */
  get size(): number {
    return this.cleanups.length
  }
}
